import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCheck } from "@fortawesome/pro-regular-svg-icons";
import {
  faCircleCheck,
  faIdCard,
  faSpinner,
} from "@fortawesome/pro-solid-svg-icons";
import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Radio,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import { themePalette } from "../../theme";
import { REQUISITION_URL } from "../../utils/urlConstants";
import { Auth0UserSerializer } from "../../recoil/types";
import { CfcPatientData } from "../Cfc/types";
import {
  GlobalSearchAccount,
  GlobalSearchError,
  GlobalSearchResult,
} from "./Services/GlobalSearchService";
import { GetTypographyContent } from "../shared/Typography/Typography";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { Button } from "../shared/Button/Button";
import FormField from "../shared/TextField/FormField";
import {
  AffiliateActionRow,
  AccountPickerWrapper,
  AttestationActions,
  AttestationBanner,
  AttestationCheckboxLabel,
  AttestationCheckboxRow,
  AttestationContent,
  AttestationSubmitButton,
  ConfirmationErrorText,
  ConfirmationFooterButton,
  ModalSpacerFooter,
  GlobalSearchTextField,
  SearchButtonGroup,
  SearchEmptyState,
  SearchClearButton,
  SearchFindButton,
  SearchFindButtonSpinner,
  SearchFindButtonWrapper,
  SearchForm,
  SearchResultsSection,
  SearchSingleRowCell,
  SearchTable,
  SearchTableActionCell,
  SearchTableCell,
  SearchTableHeaderCell,
  SearchTableRow,
  SearchTableWrapper,
  DoneFooterWrapper,
  skeletonSx,
} from "./GlobalSearchModal.styles";
import { AffiliationConfirmationContent } from "./AffiliationConfirmationContent";
import { GlobalSearchTooltip } from "./GlobalSearchTooltip";
import { TrackAnalyticsEvent } from "../../services/Analytics";
import { formatWithTimezoneOffset } from "../../utils/helpers";

type ModalStep = "loading" | "attestation" | "search";

// Track table state for reopening the modal after viewing patient details
export const SESSION_KEY_STATE = "globalSearchModalState";

interface GlobalSearchModalProps {
  onClose: () => void;
  onCheckAttestation: () => Promise<boolean>;
  onAttested: () => Promise<void>;
  onSearch: (
    firstName: string,
    lastName: string,
    dob: string,
  ) => Promise<GlobalSearchResult[] | GlobalSearchError>;
  onAffiliate: (sfid: string, accountSfid: string) => Promise<boolean>;
  onGetCfcOrderData?: (sfid: string) => Promise<CfcPatientData | null>;
  onStartCfcOrder?: (patientData: CfcPatientData) => void;
  onFetchAccounts: () => Promise<GlobalSearchAccount[] | null>;
  auth: Auth0UserSerializer;
}

const formatDate = (iso: string | null): string =>
  formatWithTimezoneOffset(iso ?? undefined) || "—";

const formatAttestedAt = (): string => {
  const now = new Date();
  const date = now.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
  const time = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${date} at ${time}`;
};

const dataColumns = [
  { key: "patient", label: "PATIENT", width: "20%" },
  { key: "dob", label: "DATE OF BIRTH" },
  { key: "due_date", label: "DUE DATE" },
  { key: "location", label: "LOCATION", width: "15%" },
  { key: "provider", label: "ORDERING PROVIDER", width: "20%" },
];

const preAffiliationColumns = [
  { key: "select", label: "", width: "5%" },
  ...dataColumns,
];

const postAffiliationColumns = [
  ...dataColumns,
  { key: "actions", label: "ACTIONS", width: "10%" },
];

// Conditional tooltip for long text that overflows the cell
const OverflowTooltip: React.FC<{
  title: string;
  children: React.ReactElement;
}> = ({ title, children }) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const ref = React.useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (el) setIsOverflowed(el.scrollWidth > el.clientWidth);
  }, [title]);

  return (
    <GlobalSearchTooltip title={isOverflowed ? title : ""}>
      {React.cloneElement(children, { ref })}
    </GlobalSearchTooltip>
  );
};

export const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({
  onClose,
  onCheckAttestation,
  onAttested,
  onSearch,
  onAffiliate,
  onGetCfcOrderData,
  onStartCfcOrder,
  onFetchAccounts,
  auth,
}) => {
  const history = useHistory();

  const restoredState = useMemo<{
    firstName: string;
    lastName: string;
    dob: string;
    detailSfid?: string;
    hasAffiliationChanged?: boolean;
  } | null>(() => {
    const raw = sessionStorage.getItem(SESSION_KEY_STATE);
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);
  const [step, setStep] = useState<ModalStep>(
    restoredState ? "search" : "loading",
  );
  const [checked, setChecked] = useState(false);
  const [firstName, setFirstName] = useState(restoredState?.firstName ?? "");
  const [lastName, setLastName] = useState(restoredState?.lastName ?? "");
  const [dob, setDob] = useState(restoredState?.dob ?? "");
  const [results, setResults] = useState<GlobalSearchResult[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<GlobalSearchError | false>(
    false,
  );
  const [confirmingResult, setConfirmingResult] =
    useState<GlobalSearchResult | null>(null);
  const [isSubmittingAffiliation, setIsSubmittingAffiliation] = useState(false);
  const [affiliationError, setAffiliationError] = useState(false);
  const [hasAffiliationChanged, setHasAffiliationChanged] = useState(
    Boolean(restoredState?.hasAffiliationChanged),
  );
  const [selectedReqSfid, setSelectedReqSfid] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<GlobalSearchAccount[]>([]);
  const [selectedAccountSfid, setSelectedAccountSfid] = useState<string | null>(
    null,
  );
  const [affiliatedResult, setAffiliatedResult] =
    useState<GlobalSearchResult | null>(null);
  const [cfcOrderData, setCfcOrderData] = useState<CfcPatientData | null>(null);

  const autoSelectSingleResult = (data: GlobalSearchResult[]) => {
    const unaffiliated = data.filter((r) => !r.is_affiliated);
    if (unaffiliated.length === 1) setSelectedReqSfid(unaffiliated[0].sfid);
  };

  useEffect(() => {
    sessionStorage.removeItem(SESSION_KEY_STATE);
    if (restoredState) {
      setIsSearching(true);
      onSearch(
        restoredState.firstName,
        restoredState.lastName,
        restoredState.dob,
      )
        .then((data) => {
          if (typeof data === "string") {
            setSearchError(data);
          } else {
            const restoredDetailResult = restoredState.detailSfid
              ? data.find((r) => r.sfid === restoredState.detailSfid)
              : undefined;
            if (restoredDetailResult?.is_affiliated) {
              setAffiliatedResult(restoredDetailResult);
            } else {
              setResults(data);
              autoSelectSingleResult(data);
            }
          }
        })
        .catch(() => setSearchError("network"))
        .finally(() => setIsSearching(false));
    } else {
      onCheckAttestation()
        .then((isValid) => {
          setStep(isValid ? "search" : "attestation");
        })
        .catch(() => setStep("attestation"));
    }
    onFetchAccounts()
      .then((data) => {
        if (data && data.length > 0) {
          setAccounts(data);
          setSelectedAccountSfid(data[0].sfid);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!affiliatedResult || !onGetCfcOrderData) {
      setCfcOrderData(null);
      return undefined;
    }

    let cancelled = false;
    setCfcOrderData(null);
    onGetCfcOrderData(affiliatedResult.sfid)
      .then((patientData) => {
        if (!cancelled) {
          setCfcOrderData(patientData);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCfcOrderData(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [affiliatedResult, onGetCfcOrderData]);

  // Validate form inputs
  const MIN_NAME_LENGTH = 2;
  const isFirstNameValid = firstName.trim().length >= MIN_NAME_LENGTH;
  const isLastNameValid = lastName.trim().length >= MIN_NAME_LENGTH;
  const isDobValid = dob !== "";
  const isFormValid = isFirstNameValid && isLastNameValid && isDobValid;
  const displayName =
    `${auth.first_name} ${auth.last_name}`.trim() || auth.username;
  const isPostAffiliation = affiliatedResult !== null;
  const tableColumns = isPostAffiliation
    ? postAffiliationColumns
    : preAffiliationColumns;
  const tableResults = isPostAffiliation ? [affiliatedResult] : results;
  const colSpan = tableColumns.length;
  const selectedAccountName =
    accounts.find((a) => a.sfid === selectedAccountSfid)?.sfname ?? "";

  const handleSubmitAttestation = async () => {
    await onAttested();
    setStep("search");
  };

  const handleSearch = async () => {
    TrackAnalyticsEvent("global_search_find", auth.token, auth.username);
    setIsSearching(true);
    setSearchError(false);
    setAffiliatedResult(null);
    setSelectedReqSfid(null);
    try {
      const data = await onSearch(firstName, lastName, dob);
      if (typeof data === "string") {
        setSearchError(data);
        setResults(null);
      } else {
        setResults(data);
        autoSelectSingleResult(data);
      }
    } catch {
      setSearchError("network");
      setResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  const handleClear = () => {
    setFirstName("");
    setLastName("");
    setDob("");
    setResults(null);
    setSearchError(false);
    setAffiliatedResult(null);
    setSelectedReqSfid(null);
    sessionStorage.removeItem(SESSION_KEY_STATE);
  };

  const handleViewPatient = (sfid: string) => {
    TrackAnalyticsEvent(
      "global_search_view_details",
      auth.token,
      auth.username,
    );
    sessionStorage.setItem(
      SESSION_KEY_STATE,
      JSON.stringify({
        firstName,
        lastName,
        dob,
        detailSfid: sfid,
        hasAffiliationChanged,
      }),
    );
    history.push(`${REQUISITION_URL}/${sfid}`);
  };

  const handleOpenConfirmation = (result: GlobalSearchResult) => {
    setAffiliationError(false);
    setIsSubmittingAffiliation(false);
    setConfirmingResult(result);
  };

  const handleAddPatientClick = () => {
    if (!selectedReqSfid || !results) return;
    const selected = results.find((r) => r.sfid === selectedReqSfid);
    if (selected) handleOpenConfirmation(selected);
  };

  const handleCancelConfirmation = () => {
    setAffiliationError(false);
    setConfirmingResult(null);
  };

  const handleConfirmAffiliation = async () => {
    if (confirmingResult === null) return;
    const { sfid } = confirmingResult;
    if (!selectedAccountSfid) return;
    setIsSubmittingAffiliation(true);
    setAffiliationError(false);
    const ok = await onAffiliate(sfid, selectedAccountSfid);
    setIsSubmittingAffiliation(false);
    if (!ok) {
      setAffiliationError(true);
      return;
    }
    TrackAnalyticsEvent("global_search_affiliate", auth.token, auth.username);
    setAffiliatedResult({ ...confirmingResult, is_affiliated: true });
    setResults(null);
    setSelectedReqSfid(null);
    setConfirmingResult(null);
    setHasAffiliationChanged(true);
  };

  return (
    <Dialog
      fullWidth
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": { maxWidth: "1000px" },
      }}
      open
      PaperProps={{ sx: { borderRadius: "8px" } }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          backgroundColor: themePalette.neutral50,
          fontFamily: "bold",
          fontSize: "18px",
          fontWeight: 700,
          color: themePalette.neutral700,
        }}
      >
        Referred Patient Search
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        {step === "loading" && (
          <SearchEmptyState>
            <SpinnerLoading />
          </SearchEmptyState>
        )}
        {step === "attestation" && (
          <AttestationContent>
            <AttestationBanner>
              {GetTypographyContent({
                content: "IMPORTANT HIPAA ATTESTATION",
                size: "large",
                isBold: true,
                isCaps: true,
                isItalic: false,
                padding: "0 0 4px 0",
              })}
              {GetTypographyContent({
                content:
                  "Please read carefully and, if appropriate, indicate your agreement below.",
                size: "medium",
                isBold: true,
                isCaps: false,
                isItalic: false,
                padding: "0 0 16px 0",
              })}
              {GetTypographyContent({
                content: (
                  <>
                    Because you are a health care provider and we share a
                    patient in common, HIPAA permits us to disclose patient PHI
                    to you for &ldquo;treatment&rdquo; purposes (as defined at
                    45 CFR 164.501).
                  </>
                ),
                size: "medium",
                isBold: false,
                isCaps: false,
                isItalic: false,
                padding: "0 0 12px 0",
              })}
              {GetTypographyContent({
                content:
                  "By checking the box below and submitting this attestation, you agree that you will only use the functionality in this platform to identify and secure patient information that is needed for HIPAA-permitted treatment purposes, and that your subsequent use, storage, and/or disclosure, of such information will comply with all applicable HIPAA and other state and federal regulations.",
                size: "medium",
                isBold: false,
                isCaps: false,
                isItalic: false,
              })}
              <AttestationCheckboxRow>
                <Checkbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  sx={{
                    padding: 0,
                    color: themePalette.neutral400,
                    "&.Mui-checked": { color: themePalette.redMedium },
                  }}
                />
                <AttestationCheckboxLabel onClick={() => setChecked((v) => !v)}>
                  {GetTypographyContent({
                    content: (
                      <>
                        Signed and submitted by:
                        <br />
                        {displayName} on {formatAttestedAt()}
                      </>
                    ),
                    size: "medium",
                    isBold: false,
                    isCaps: false,
                    isItalic: false,
                  })}
                </AttestationCheckboxLabel>
              </AttestationCheckboxRow>
              <AttestationActions>
                <AttestationSubmitButton
                  disabled={!checked}
                  onClick={handleSubmitAttestation}
                >
                  SUBMIT
                </AttestationSubmitButton>
              </AttestationActions>
            </AttestationBanner>
          </AttestationContent>
        )}
        {step === "search" && (
          <>
            <SearchForm>
              <GlobalSearchTextField
                label="First Name"
                variant="standard"
                size="small"
                focused
                fullWidth
                required
                disabled={
                  confirmingResult !== null || searchError === "blocked"
                }
                $invalidAsterisk={!isFirstNameValid}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <GlobalSearchTextField
                label="Last Name"
                variant="standard"
                size="small"
                focused
                fullWidth
                required
                disabled={
                  confirmingResult !== null || searchError === "blocked"
                }
                $invalidAsterisk={!isLastNameValid}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <GlobalSearchTextField
                label="Date of Birth"
                variant="standard"
                size="small"
                type="date"
                focused
                fullWidth
                required
                disabled={
                  confirmingResult !== null || searchError === "blocked"
                }
                $invalidAsterisk={!isDobValid}
                $noDateValue={!dob}
                value={dob}
                onChange={(e) => setDob(e.target.value)}
              />
              <SearchButtonGroup>
                <GlobalSearchTooltip
                  title={
                    isFormValid
                      ? ""
                      : "All input fields are required and must be valid"
                  }
                >
                  <SearchFindButtonWrapper $loading={isSearching}>
                    <SearchFindButton
                      label="FIND"
                      onClick={handleSearch}
                      size="medium"
                      reverse
                      backgroundColor="redMedium"
                      disabled={
                        !isFormValid ||
                        confirmingResult !== null ||
                        isSearching ||
                        searchError === "blocked"
                      }
                    />
                    {isSearching && (
                      <SearchFindButtonSpinner>
                        <FontAwesomeIcon icon={faSpinner} spin />
                      </SearchFindButtonSpinner>
                    )}
                  </SearchFindButtonWrapper>
                </GlobalSearchTooltip>
                <SearchClearButton
                  onClick={handleClear}
                  type="button"
                  disabled={
                    confirmingResult !== null || searchError === "blocked"
                  }
                >
                  <CancelIcon fontSize="inherit" />
                </SearchClearButton>
              </SearchButtonGroup>
            </SearchForm>
            {confirmingResult !== null && (
              <AffiliationConfirmationContent
                mode="affiliate"
                patient={confirmingResult}
                accountName={selectedAccountName}
                isSubmitting={isSubmittingAffiliation}
                error={affiliationError}
                onConfirm={handleConfirmAffiliation}
                onCancel={handleCancelConfirmation}
              />
            )}
            {confirmingResult === null &&
              (isSearching ||
                searchError ||
                results !== null ||
                affiliatedResult !== null) && (
                <SearchResultsSection>
                  {GetTypographyContent({
                    content: "SEARCH RESULTS",
                    size: "small",
                    isBold: false,
                    isCaps: true,
                    isItalic: false,
                    textColor: "neutral400",
                    padding: "0 0 8px 8px",
                  })}
                  <SearchTableWrapper>
                    <SearchTable>
                      <thead>
                        <tr>
                          {tableColumns.map(({ key, label, width }) => (
                            <SearchTableHeaderCell
                              key={key}
                              style={{
                                width,
                                ...(key === "actions"
                                  ? { textAlign: "center" }
                                  : {}),
                              }}
                            >
                              {label &&
                                GetTypographyContent({
                                  content: label,
                                  size: "small",
                                  isBold: true,
                                  isCaps: false,
                                  isItalic: false,
                                  textColor: "neutral400",
                                })}
                            </SearchTableHeaderCell>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {isSearching && (
                          <SearchTableRow>
                            {tableColumns.map(({ key }) => {
                              const Cell =
                                key === "actions"
                                  ? SearchTableActionCell
                                  : SearchTableCell;
                              return (
                                <Cell key={key}>
                                  {key !== "select" && (
                                    <Skeleton
                                      variant="rectangular"
                                      animation="wave"
                                      width="80%"
                                      height={16}
                                      sx={{
                                        ...skeletonSx,
                                        ...(key === "actions"
                                          ? { display: "inline-block" }
                                          : {}),
                                      }}
                                    />
                                  )}
                                </Cell>
                              );
                            })}
                          </SearchTableRow>
                        )}
                        {!isSearching &&
                          (searchError ||
                            (tableResults !== null &&
                              tableResults.length === 0)) && (
                            <SearchTableRow>
                              <SearchSingleRowCell colSpan={colSpan}>
                                {searchError ? (
                                  <ConfirmationErrorText>
                                    {searchError === "blocked"
                                      ? "Your account has been blocked. Please call +1(650) 460-2551 to continue using referred patient search."
                                      : "There was a network error. Please try again."}
                                  </ConfirmationErrorText>
                                ) : (
                                  GetTypographyContent({
                                    content:
                                      "We couldn't find any patients with these details. Please check your input and try again.",
                                    size: "small",
                                    isBold: false,
                                    isCaps: false,
                                    isItalic: false,
                                    textColor: "neutral700",
                                  })
                                )}
                              </SearchSingleRowCell>
                            </SearchTableRow>
                          )}
                        {!isSearching &&
                          tableResults?.map((result) => (
                            <SearchTableRow key={result.sfid}>
                              {!isPostAffiliation && (
                                <SearchTableActionCell>
                                  {result.is_affiliated ? (
                                    <GlobalSearchTooltip title="Patient is added to your list">
                                      <span>
                                        <FontAwesomeIcon
                                          icon={faCircleCheck}
                                          color={themePalette.greenMedium}
                                        />
                                      </span>
                                    </GlobalSearchTooltip>
                                  ) : (
                                    <Radio
                                      checked={selectedReqSfid === result.sfid}
                                      onChange={() =>
                                        setSelectedReqSfid(result.sfid)
                                      }
                                      size="small"
                                      sx={{
                                        padding: 0,
                                        color: themePalette.neutral400,
                                        "&.Mui-checked": {
                                          color: themePalette.redMedium,
                                        },
                                      }}
                                    />
                                  )}
                                </SearchTableActionCell>
                              )}
                              <OverflowTooltip
                                title={`${result.first_name} ${result.last_name}`}
                              >
                                <SearchTableCell>
                                  {result.first_name} {result.last_name}
                                </SearchTableCell>
                              </OverflowTooltip>
                              <SearchTableCell>
                                {formatDate(result.date_of_birth)}
                              </SearchTableCell>
                              <SearchTableCell>
                                {formatDate(result.due_date)}
                              </SearchTableCell>
                              <OverflowTooltip
                                title={
                                  [result.city, result.state]
                                    .filter(Boolean)
                                    .join(", ") || ""
                                }
                              >
                                <SearchTableCell>
                                  {[result.city, result.state]
                                    .filter(Boolean)
                                    .join(", ") || "—"}
                                </SearchTableCell>
                              </OverflowTooltip>
                              <OverflowTooltip
                                title={result.physician_name ?? ""}
                              >
                                <SearchTableCell>
                                  {result.physician_name ?? "—"}
                                </SearchTableCell>
                              </OverflowTooltip>
                              {isPostAffiliation && (
                                <SearchTableActionCell>
                                  <GlobalSearchTooltip title="See patient details">
                                    <IconButton
                                      onClick={() =>
                                        handleViewPatient(result.sfid)
                                      }
                                      size="small"
                                      sx={{
                                        padding: 0,
                                        "&:hover": { opacity: 0.7 },
                                        "&:active .svg-inline--fa": {
                                          color: themePalette.greenDark,
                                        },
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faIdCard}
                                        color={
                                          themePalette.resultsNegativeGreen
                                        }
                                      />
                                    </IconButton>
                                  </GlobalSearchTooltip>
                                  {cfcOrderData && onStartCfcOrder && (
                                    <GlobalSearchTooltip title="Order Unity Confirm test">
                                      <IconButton
                                        onClick={() => {
                                          onStartCfcOrder(cfcOrderData);
                                        }}
                                        size="small"
                                        sx={{
                                          padding: 0,
                                          marginLeft: "20px",
                                          "&:hover": { opacity: 0.7 },
                                          "&:active .svg-inline--fa": {
                                            color: themePalette.redDark,
                                          },
                                        }}
                                      >
                                        <FontAwesomeIcon
                                          icon={faFileCheck}
                                          color={themePalette.redMedium}
                                        />
                                      </IconButton>
                                    </GlobalSearchTooltip>
                                  )}
                                </SearchTableActionCell>
                              )}
                            </SearchTableRow>
                          ))}
                      </tbody>
                    </SearchTable>
                  </SearchTableWrapper>
                  {!isPostAffiliation &&
                    !isSearching &&
                    !searchError &&
                    results !== null &&
                    results.length > 0 && (
                      <AffiliateActionRow>
                        <AccountPickerWrapper>
                          <FormField
                            fieldTitle="Select a clinic to affiliate the patient to"
                            hideTitle
                            fieldType="autocomplete"
                            autoCompleteDropdownStyle
                            autoCompleteTextFieldVariant="outlined"
                            autoCompleteOptions={accounts.map((a) => a.sfname)}
                            fieldValue={
                              accounts.find(
                                (a) => a.sfid === selectedAccountSfid,
                              )?.sfname ?? ""
                            }
                            onFieldChange={(val) => {
                              const account = accounts.find(
                                (a) => a.sfname === val,
                              );
                              setSelectedAccountSfid(account?.sfid ?? null);
                            }}
                            edit={Boolean(selectedReqSfid)}
                          />
                        </AccountPickerWrapper>
                        <GlobalSearchTooltip
                          title={
                            !selectedReqSfid || !selectedAccountSfid
                              ? "Select a patient and an account to add"
                              : ""
                          }
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              height: "44px",
                            }}
                          >
                            <Button
                              label="ADD PATIENT TO MY LIST"
                              onClick={handleAddPatientClick}
                              size="small"
                              disabled={
                                !selectedReqSfid || !selectedAccountSfid
                              }
                            />
                          </span>
                        </GlobalSearchTooltip>
                      </AffiliateActionRow>
                    )}
                </SearchResultsSection>
              )}
            {hasAffiliationChanged && confirmingResult === null && (
              <DoneFooterWrapper>
                <ConfirmationFooterButton
                  onClick={onClose}
                  style={{ fontSize: "18px" }}
                >
                  DONE
                </ConfirmationFooterButton>
              </DoneFooterWrapper>
            )}
            <ModalSpacerFooter />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
